import { useEffect, useState } from "react";
import {
  useParams,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useStripeInvoices } from "@/providers/StripeInvoiceProvider";
import { useWorkOrders } from "@/providers/WorkOrdersProvider";
import { InvoiceDto } from "@shared/types/InvoiceDto";
import { WorkOrderDto } from "@shared/types/WorkOrderDto";
import { InvoiceHeader } from "./InvoiceHeader";
import { InvoiceStats } from "./InvoiceStats";
import { WorkOrderDetails } from "./WorkOrderDetails";

export function InvoiceDetailPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { invoice?: InvoiceDto } | null;
  const [selectedWorkOrder, setSelectedWorkOrder] =
    useState<WorkOrderDto | null>(null);
  const [workOrderLoading, setWorkOrderLoading] = useState(false);
  const [invoice, setInvoice] = useState<InvoiceDto | null>(
    locationState?.invoice || null,
  );

  const {
    invoices,
    isLoading: invoicesLoading,
    fetchInvoices,
  } = useStripeInvoices();
  const { selectWorkOrderByInvoiceId } = useWorkOrders();

  useEffect(() => {
    if (!invoice && invoices.length === 0 && !invoicesLoading) {
      fetchInvoices();
    }
  }, [invoice, invoices.length, invoicesLoading, fetchInvoices]);

  useEffect(() => {
    if (!invoice && invoices.length > 0 && invoiceId) {
      const found = invoices.find((inv) => inv.id === invoiceId);
      setInvoice(found || null);
    }
  }, [invoice, invoices, invoiceId]);

  useEffect(() => {
    if (invoice?.workOrderId && invoiceId) {
      setWorkOrderLoading(true);
      selectWorkOrderByInvoiceId(invoiceId)
        .then((workOrder) => {
          setSelectedWorkOrder(workOrder);
        })
        .catch((err) => {
          console.error("Error selecting work order by invoice id:", err);
        })
        .finally(() => {
          setWorkOrderLoading(false);
        });
    }
  }, [invoice?.workOrderId, invoiceId, selectWorkOrderByInvoiceId]);

  function handleBack() {
    navigate("/invoices");
  }

  function handleViewInvoice() {
    if (invoice?.downloadUrl) {
      window.open(invoice.downloadUrl, "_blank", "noopener,noreferrer");
    }
  }

  if (!invoice && invoicesLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!invoice && !invoicesLoading) {
    return <Navigate to="/invoices" replace />;
  }

  if (!invoice) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      <InvoiceHeader
        invoice={invoice}
        onBack={handleBack}
        onViewInvoice={handleViewInvoice}
      />

      <InvoiceStats invoice={invoice} />

      <WorkOrderDetails
        workOrder={selectedWorkOrder}
        isLoading={workOrderLoading}
      />
    </motion.div>
  );
}
